/**
 * @file The only place a label is drawn. Consumers: the attachments panel file
 * list, communication log rows, and the files listed inside an expanded log
 * entry.
 *
 * Iteration runs over the set, not over `labelIds`, which is what makes the
 * administrator's declared order the rendered order and makes an id the set no
 * longer holds render nothing rather than throw — the invariant archiving
 * depends on. A group absent from `groupOrder` is undeclared and renders
 * nothing. A stored colour outside `LABEL_COLORS` is dropped before it reaches
 * `Tag`: a non-preset value sends `Tag` down a hardcoded-lightness branch that
 * writes a literal hex into `style.backgroundColor`, ignoring the dark
 * algorithm. The name is the `Tag`'s own child rather than a nested
 * `Typography.Text`, which would repaint it `colorText` and cost the colour.
 */

import { type FC } from 'react';
import { Flex, Tag } from 'antd';
import { type Snapshot } from 'valtio';
import { LABEL_COLORS, type LabelId, type LabelSet } from '#types';

const PALETTE: ReadonlySet<string> = new Set(LABEL_COLORS);

const paletteColor = (color: string): string | undefined => (PALETTE.has(color) ? color : undefined);

export const LabelTags: FC<{
    set: Snapshot<LabelSet>;
    labelIds: readonly LabelId[] | undefined;
}> = ({ set, labelIds }) => {
    const assigned = new Set(labelIds ?? []);
    if (assigned.size === 0) return null;

    const applied = set.groupOrder.flatMap(groupId =>
        (set.groups[groupId]?.labels ?? []).filter(label => assigned.has(label.id)),
    );
    if (applied.length === 0) return null;

    return (
        <Flex wrap gap={5} align="center">
            {applied.map(label => (
                <Tag key={label.id} color={paletteColor(label.color)}>
                    {label.name}
                </Tag>
            ))}
        </Flex>
    );
};
